export interface MerchantComplianceProfile {
  gstNumber?: string;
  fssaiNumber?: string;
  tradeLicenseNumber?: string;
  ownerName?: string;
  businessName?: string;
  address?: string;
  city?: string;
  latitude?: string;
  longitude?: string;
  sources: string[];
}

export type MerchantDocumentSlotStatus = 'approved' | 'pending' | 'missing' | 'rejected';

export type MerchantDocumentSlot = {
  status: MerchantDocumentSlotStatus;
  /** Document number from the best matching upload (approved preferred). */
  documentNumber?: string;
  /** True when an approved upload with a number exists. */
  approved: boolean;
};

export type MerchantDocumentAvailability = {
  gst: MerchantDocumentSlotStatus;
  fssai: MerchantDocumentSlotStatus;
  tradeLicense: MerchantDocumentSlotStatus;
  ownerId: MerchantDocumentSlotStatus;
  gstSlot: MerchantDocumentSlot;
  fssaiSlot: MerchantDocumentSlot;
  tradeLicenseSlot: MerchantDocumentSlot;
  ownerIdSlot: MerchantDocumentSlot;
  hasApprovedGstOrFssai: boolean;
  approvedGstNumber?: string;
  approvedFssaiNumber?: string;
  approvedTradeLicenseNumber?: string;
  approvedOwnerIdNumber?: string;
  pendingCount: number;
  missingCount: number;
  rejectedCount: number;
  approvedCount: number;
  attentionCount: number;
};

type DocRow = {
  config?: { code?: string; name?: string };
  uploaded?: { documentNumber?: string; status?: string };
  documentStatus?: string;
};

type PartnerDocumentPayload = {
  documents?: DocRow[];
  onboarding?: { formData?: Record<string, unknown>; status?: string };
};

function normCode(code: string) {
  return code.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Normalize API statuses: APPROVED / WAITINGFORAPPROVAL / DENIED / EXPIRED / pending / rejected. */
function normalizeDocStatus(raw: string): MerchantDocumentSlotStatus | 'unknown' {
  const s = raw.toLowerCase().replace(/[^a-z]/g, '');
  if (s === 'approved') return 'approved';
  if (s === 'denied' || s === 'rejected') return 'rejected';
  if (s === 'expired') return 'rejected';
  if (
    s === 'pending'
    || s === 'waitingforapproval'
    || s === 'waiting'
  ) return 'pending';
  if (s === 'notuploaded' || s === '') return 'missing';
  return 'unknown';
}

function resolveSlot(docs: DocRow[], matchers: string[]): MerchantDocumentSlot {
  let best: MerchantDocumentSlot = { status: 'missing', approved: false };
  let sawPending = false;
  let sawRejected = false;
  let pendingNumber: string | undefined;
  let rejectedNumber: string | undefined;

  for (const row of docs) {
    const code = normCode(row.config?.code || row.config?.name || '');
    if (!matchers.some((m) => code.includes(m))) continue;

    const fromAdmin = normalizeDocStatus(String(row.documentStatus || ''));
    const fromUpload = normalizeDocStatus(String(row.uploaded?.status || ''));
    const status = fromAdmin !== 'unknown' && fromAdmin !== 'missing'
      ? fromAdmin
      : fromUpload !== 'unknown'
        ? fromUpload
        : 'missing';
    const num = row.uploaded?.documentNumber?.trim() || undefined;

    if (status === 'approved' && num) {
      return { status: 'approved', documentNumber: num, approved: true };
    }
    if (status === 'approved' && !num) {
      // Approved file without identifier — still count as pending apply readiness.
      sawPending = true;
      continue;
    }
    if (status === 'rejected') {
      sawRejected = true;
      if (num && !rejectedNumber) rejectedNumber = num;
      continue;
    }
    if (status === 'pending' || num) {
      sawPending = true;
      if (num && !pendingNumber) pendingNumber = num;
    }
  }

  if (sawRejected) {
    return { status: 'rejected', documentNumber: rejectedNumber, approved: false };
  }
  if (sawPending) {
    return { status: 'pending', documentNumber: pendingNumber, approved: false };
  }
  return best;
}

function countByStatus(statuses: MerchantDocumentSlotStatus[]) {
  return {
    approvedCount: statuses.filter((s) => s === 'approved').length,
    pendingCount: statuses.filter((s) => s === 'pending').length,
    missingCount: statuses.filter((s) => s === 'missing').length,
    rejectedCount: statuses.filter((s) => s === 'rejected').length,
  };
}

export function getMerchantDocumentAvailability(data: PartnerDocumentPayload): MerchantDocumentAvailability {
  const docs = data.documents || [];
  const gstSlot = resolveSlot(docs, ['gst', 'gstin']);
  const fssaiSlot = resolveSlot(docs, ['fssai', 'foodlicense']);
  const tradeLicenseSlot = resolveSlot(docs, ['trade', 'tradelicense', 'shopact']);
  const ownerIdSlot = resolveSlot(docs, ['owner', 'ownerid', 'aadhar', 'pan', 'identity']);

  const statuses = [gstSlot.status, fssaiSlot.status, tradeLicenseSlot.status, ownerIdSlot.status];
  const counts = countByStatus(statuses);
  const approvedGstNumber = gstSlot.approved ? gstSlot.documentNumber : undefined;
  const approvedFssaiNumber = fssaiSlot.approved ? fssaiSlot.documentNumber : undefined;
  const approvedTradeLicenseNumber = tradeLicenseSlot.approved ? tradeLicenseSlot.documentNumber : undefined;
  const approvedOwnerIdNumber = ownerIdSlot.approved ? ownerIdSlot.documentNumber : undefined;

  return {
    gst: gstSlot.status,
    fssai: fssaiSlot.status,
    tradeLicense: tradeLicenseSlot.status,
    ownerId: ownerIdSlot.status,
    gstSlot,
    fssaiSlot,
    tradeLicenseSlot,
    ownerIdSlot,
    hasApprovedGstOrFssai: Boolean(approvedGstNumber || approvedFssaiNumber),
    approvedGstNumber,
    approvedFssaiNumber,
    approvedTradeLicenseNumber,
    approvedOwnerIdNumber,
    ...counts,
    attentionCount: counts.pendingCount + counts.missingCount + counts.rejectedCount,
  };
}

/** Values from approved merchant documents only — safe to apply to restaurant fields. */
export function extractApprovedDocumentCompliance(data: PartnerDocumentPayload): MerchantComplianceProfile {
  const availability = getMerchantDocumentAvailability(data);
  const sources: string[] = [];

  if (availability.approvedGstNumber) sources.push('GST document');
  if (availability.approvedFssaiNumber) sources.push('FSSAI document');
  if (availability.approvedTradeLicenseNumber) sources.push('trade license document');

  return {
    gstNumber: availability.approvedGstNumber,
    fssaiNumber: availability.approvedFssaiNumber,
    tradeLicenseNumber: availability.approvedTradeLicenseNumber,
    sources,
  };
}

export function extractMerchantCompliance(data: PartnerDocumentPayload): MerchantComplianceProfile {
  const docs = data.documents || [];
  const form = (data.onboarding?.formData || {}) as Record<string, unknown>;
  const sources: string[] = [];
  const availability = getMerchantDocumentAvailability({ documents: docs });

  const gstFromDoc = availability.approvedGstNumber;
  const fssaiFromDoc = availability.approvedFssaiNumber;
  const tradeFromDoc = availability.approvedTradeLicenseNumber;

  const gstNumber = gstFromDoc || String(form.gst_number || form.gstNumber || '').trim() || undefined;
  const fssaiNumber = fssaiFromDoc || String(form.fssai_license || form.fssaiNumber || form.fssai || '').trim() || undefined;
  const tradeLicenseNumber = tradeFromDoc || String(form.trade_license || form.tradeLicense || '').trim() || undefined;

  if (gstFromDoc) sources.push('GST document');
  else if (gstNumber) sources.push('onboarding form');
  if (fssaiFromDoc) sources.push('FSSAI document');
  else if (fssaiNumber) sources.push('onboarding form');
  if (tradeFromDoc) sources.push('trade license document');
  else if (tradeLicenseNumber) sources.push('onboarding form');

  const ownerName = String(form.owner_name || form.ownerName || '').trim() || undefined;
  const businessName = String(form.business_name || form.businessName || '').trim() || undefined;
  const address = String(form.address || form.addressLine || '').trim() || undefined;
  const city = String(form.city || '').trim() || undefined;
  const lat = form.latitude ?? form.lat;
  const lng = form.longitude ?? form.lng;

  return {
    gstNumber,
    fssaiNumber,
    tradeLicenseNumber,
    ownerName,
    businessName,
    address,
    city,
    latitude: lat != null && lat !== '' ? String(lat) : undefined,
    longitude: lng != null && lng !== '' ? String(lng) : undefined,
    sources: [...new Set(sources)],
  };
}

export function hasMerchantCompliance(profile: MerchantComplianceProfile) {
  return !!(profile.gstNumber || profile.fssaiNumber || profile.tradeLicenseNumber
    || profile.ownerName || profile.businessName || profile.address);
}

/** True when GST / FSSAI / trade license numbers are available to copy onto the restaurant. */
export function hasMerchantComplianceNumbers(profile: MerchantComplianceProfile) {
  return !!(
    String(profile.gstNumber || '').trim()
    || String(profile.fssaiNumber || '').trim()
    || String(profile.tradeLicenseNumber || '').trim()
  );
}

export function formatMerchantDocumentStatus(status: MerchantDocumentSlotStatus): string {
  if (status === 'approved') return 'Approved';
  if (status === 'pending') return 'Pending review';
  if (status === 'rejected') return 'Rejected / expired';
  return 'Not uploaded';
}

export function merchantDocumentStatusColor(
  status: MerchantDocumentSlotStatus,
): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'approved') return 'success';
  if (status === 'pending') return 'warning';
  if (status === 'rejected') return 'error';
  return 'default';
}

export type ComplianceFieldUpdate = {
  field: 'gstNumber' | 'fssaiNumber' | 'tradeLicenseNumber';
  label: string;
  current: string;
  next: string;
  changed: boolean;
};

/** Preview GST/FSSAI/trade license changes from approved documents only. */
export function buildApprovedComplianceUpdates(
  form: { gstNumber: string; fssaiNumber: string; tradeLicenseNumber: string },
  approved: Pick<MerchantComplianceProfile, 'gstNumber' | 'fssaiNumber' | 'tradeLicenseNumber'>,
): ComplianceFieldUpdate[] {
  const updates: ComplianceFieldUpdate[] = [];
  const push = (
    field: ComplianceFieldUpdate['field'],
    label: string,
    next?: string,
  ) => {
    const value = String(next || '').trim();
    if (!value) return;
    const current = String(form[field] || '').trim();
    updates.push({
      field,
      label,
      current: current || '—',
      next: value,
      changed: current !== value,
    });
  };
  push('gstNumber', 'GST Number', approved.gstNumber);
  push('fssaiNumber', 'FSSAI Number', approved.fssaiNumber);
  push('tradeLicenseNumber', 'Trade License', approved.tradeLicenseNumber);
  return updates;
}

/** Only fields that will actually change when Apply runs. */
export function buildChangedComplianceUpdates(
  form: { gstNumber: string; fssaiNumber: string; tradeLicenseNumber: string },
  approved: Pick<MerchantComplianceProfile, 'gstNumber' | 'fssaiNumber' | 'tradeLicenseNumber'>,
): ComplianceFieldUpdate[] {
  return buildApprovedComplianceUpdates(form, approved).filter((row) => row.changed);
}
